import React from 'react';
import { useHref, useLinkClickHandler } from 'react-router-dom';
import { Button, Flex, theme } from 'antd';

import BillingUpcomingTagIcon from '../../../assets/icons/billing-upcoming-tag-icon.svg';
import BillingUpcomingBadgeIcon from '../../../assets/icons/billing-upcoming-badge-icon.svg';
import BillingDoneTagIcon from '../../../assets/icons/billing-done-tag-icon.svg';

import { dates } from '~library/dates';

import { useGetInvoicesQuery } from '../api/billingApiSlice';
import { formatCentsToDollar } from '../../../assets/currencies';
import { useEvent } from '../../events/hooks/useEvent';

import InvoiceStatus from '../history/InvoiceStatus';

export const BillingOverviewBillingActivity = ({ user }) => {
  const { data: invoicesList, isError: isErrorLoadingInvoices } = useGetInvoicesQuery();
  const { token } = theme.useToken();
  const billingHistoryHref = useHref('/billing/history');
  const navigateToBillingHistory = useLinkClickHandler('/billing/history');

  const { track } = useEvent();

  const onSeeAllClick = (event) => {
    track('Billing History Viewed ', { subscription_plan: user.planName, location: 'See All Button' });
    navigateToBillingHistory(event);
  };

  const dateStyle = { marginBottom: 0, fontSize: token.fontSizeSM, color: token.colorTextSecondary };

  const mapInvoice = (invoice) => {
    let isPlan = invoice.isPackageOnly === false;

    const name = isPlan ? invoice.planName : invoice.packageName;
    const hasProratedAmount =
      typeof invoice.amountPaidInCent === 'number' && invoice.amountPaidInCent !== invoice.totalInCent;
    const invoicePriceLabel = `${formatCentsToDollar(invoice.totalInCent)}${
      hasProratedAmount ? ` (charged ${formatCentsToDollar(invoice.amountPaidInCent)})` : ''
    }`;

    return (
      <Flex
        component="li"
        key={invoice.id}
        align="center"
        justify="space-between"
        style={{ marginBottom: token.marginXS }}
      >
        <Flex align="center" gap={token.marginSM}>
          <img src={invoice.status === 'paid' ? BillingDoneTagIcon : BillingUpcomingTagIcon} />
          <div>
            <p style={{ marginBottom: 0 }}>{`${name} ${isPlan ? 'Plan' : 'Package'} - ${invoicePriceLabel}`}</p>
            <p style={dateStyle}>{dates.calendarDay(invoice.date)}</p>
          </div>
        </Flex>
        <InvoiceStatus invoice={invoice} />
      </Flex>
    );
  };

  return (
    <div>
      <h3 style={{ fontWeight: token.fontWeightStrong }}>Billing Activity</h3>
      {isErrorLoadingInvoices ? (
        <Flex align="center" justify="center" style={{ height: '100%', width: '100%' }}>
          <p style={{ color: 'red' }}>An unexpected error occured, please try again by reloading the page.</p>
        </Flex>
      ) : (
        <ul style={{ marginTop: token.marginLG, paddingLeft: 0, listStyle: 'none' }}>
          {user.nextBillingAt && user.plan.estimate.planPriceInCents > 0 && (
            <>
              <Flex component="li" align="center" justify="space-between">
                <Flex align="center" gap={token.marginSM}>
                  <img src={BillingUpcomingTagIcon} />
                  <div>
                    <p style={{ marginBottom: 0 }}>{`${user.planName} Plan - ${formatCentsToDollar(
                      user.plan.estimate.planPriceInCents
                    )}`}</p>
                    {user.plan.estimate.packagesPriceInCents > 0 && (
                      <p style={{ marginBottom: 0 }}>{`Packages - ${formatCentsToDollar(
                        user.plan.estimate.packagesPriceInCents
                      )}`}</p>
                    )}
                    <p style={dateStyle}>{dates.dayOfPointInTime(user.nextBillingAt)}</p>
                  </div>
                </Flex>
                <img src={BillingUpcomingBadgeIcon}></img>
              </Flex>
              <hr style={{ marginBlock: token.marginXS }} />
            </>
          )}

          {invoicesList?.data.length > 0 ? (
            invoicesList?.data.slice(0, 3).map(mapInvoice)
          ) : (
            <Flex justify="center" style={{ marginTop: token.marginLG }}>
              <p style={{ color: token.colorTextSecondary }}>No Invoices yet.</p>
            </Flex>
          )}
        </ul>
      )}
      {invoicesList?.data.length > 0 && (
        <Button block href={billingHistoryHref} onClick={onSeeAllClick}>
          See all
        </Button>
      )}
    </div>
  );
};
